import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.ts';
import { qk } from '../lib/queryClient.ts';
import { ServiceDetailView } from '../components/ServiceDetailView.tsx';
import { recordRecentlyViewed } from '../lib/recentlyViewed.ts';
import { Skeleton, SkeletonText } from '../components/ui/Skeleton.tsx';

interface DetailPageProps {
  serviceId: string;
  favs: string[];
  onFav: (id: string) => void;
  onOpen: (id: string) => void;
}

export function DetailPage({ serviceId, favs, onFav, onOpen }: DetailPageProps) {
  const queryClient = useQueryClient();
  const detailQ = useQuery({
    queryKey: qk.services.detail(serviceId),
    queryFn: () => api.getService(serviceId),
  });
  const meQ = useQuery({ queryKey: qk.auth.me(), queryFn: () => api.me(), retry: false });
  const reviewsQ = useQuery({
    queryKey: qk.services.reviews(serviceId),
    queryFn: () => api.serviceReviews(serviceId),
  });

  // 쓰기 성공 후 캐시 무효화. 무효화 실패가 쓰기 성공을 가리지 않도록 swallow
  // (안 그러면 폼이 에러로 남아 사용자가 재제출 → 중복 리뷰 생성).
  async function refreshReviews() {
    try {
      await queryClient.invalidateQueries({ queryKey: qk.services.reviews(serviceId) });
      await queryClient.invalidateQueries({ queryKey: qk.services.detail(serviceId) });
    } catch {
      /* non-fatal */
    }
  }

  async function submitReview(rating: number, content: string) {
    await api.postReview(serviceId, { rating, content });
    await refreshReviews();
  }

  async function updateReview(id: string, rating: number, content: string) {
    await api.updateReview(id, { rating, content });
    await refreshReviews();
  }

  async function deleteReview(id: string) {
    await api.deleteReview(id);
    await refreshReviews();
  }
  const relatedQ = useQuery({
    queryKey: qk.services.related(serviceId),
    queryFn: () => api.relatedServices(serviceId),
  });

  useEffect(() => {
    api.recordView(serviceId).catch(() => undefined);
    recordRecentlyViewed(serviceId);
  }, [serviceId]);

  if (detailQ.isLoading) {
    return (
      <main className="nf-page" style={{ padding: '120px 56px', display: 'flex', flexDirection: 'column', gap: 24 }}>
        <Skeleton height={48} width="50%" />
        <Skeleton height={20} width="70%" />
        <Skeleton height={320} radius={16} />
        <SkeletonText lines={4} />
      </main>
    );
  }
  if (detailQ.error || !detailQ.data) {
    return (
      <main className="nf-page" style={{ padding: '120px 56px' }}>
        서비스를 찾을 수 없어요.{' '}
        <a href="/" style={{ color: 'var(--accent)' }}>
          홈으로
        </a>
      </main>
    );
  }

  return (
    <main className="nf-page">
      <ServiceDetailView
        service={detailQ.data}
        reviews={reviewsQ.data?.items ?? []}
        reviewsLoading={reviewsQ.isLoading}
        related={relatedQ.data?.items ?? []}
        favs={favs}
        onFav={onFav}
        onOpen={onOpen}
        canReview={!!meQ.data?.user}
        currentUserId={meQ.data?.user?.id ?? null}
        onSubmitReview={submitReview}
        onUpdateReview={updateReview}
        onDeleteReview={deleteReview}
      />
    </main>
  );
}
